import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useFiscalStore } from "@/store/fiscal-store";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  FileBox, Upload, LayoutDashboard, FileText, ShieldCheck,
  LogOut, FileBarChart2, Sun, Moon, Package, TrendingUp, Tag, Download,
  Settings, Users, BookOpen, FileSearch, Menu, User, Eye, EyeOff, RefreshCw, Database, Sparkles, Cpu,
  Calendar, Clock, CreditCard, BellRing, BarChart3, LineChart, ChevronRight, Loader2, GitBranch, History, HelpCircle, Zap, Building2, ChevronDown
} from "lucide-react";
import { db } from "@/lib/local/db";
import { syncLocalDBToStore, hydrateNotasWithItems } from "@/lib/local/sync";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MOTIVATIONAL_QUOTES } from "@/lib/quotes";
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LegalConsentModal } from "@/components/LegalConsentModal";
import { Calendar as CalendarUI } from "@/components/ui/calendar";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { ptBR } from "date-fns/locale";
import { useFolderSync } from "@/hooks/useFolderSync";

const SectionTitle = ({ children, isCollapsed }: { children: React.ReactNode, isCollapsed?: boolean }) => (
  <div className={cn(
    "px-3 pt-4 pb-1.5 text-[9px] uppercase tracking-[0.2em] font-black text-muted-foreground/60 transition-all duration-300",
    isCollapsed ? "opacity-0 h-0 py-0 overflow-hidden" : "opacity-100"
  )}>
    {children}
  </div>
);

const NavItem = ({
  to, label, icon: Icon, badge, tag, disabled, isActive, setIsMobileMenuOpen, isCollapsed
}: {
  to: string; label: string; icon: any; badge?: number; tag?: string; disabled?: boolean; isActive: boolean; setIsMobileMenuOpen: (v: boolean) => void; isCollapsed?: boolean;
}) => {
  const content = (
    <NavLink
      to={to}
      onClick={(e) => { 
        if (disabled) e.preventDefault();
        else setIsMobileMenuOpen(false);
      }}
      className={cn(
        "group relative flex items-center py-3 transition-all duration-500 rounded-2xl mx-2 mb-1 shadow-sm overflow-hidden border border-transparent",
        isActive 
          ? "bg-white text-primary shadow-2xl scale-[1.02] z-10 dark:bg-white dark:text-primary dark:shadow-primary/10" 
          : "bg-primary text-white hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/20 dark:bg-primary/90 dark:text-white/90 dark:border-primary/10",
        disabled && "opacity-50 cursor-not-allowed grayscale",
        isCollapsed ? "justify-center w-12 mx-auto px-0 h-12" : "gap-4 px-4"
      )}
    >
      <Icon className={cn(
        "size-5 shrink-0 transition-all duration-500", 
        isActive ? "text-primary scale-110" : "text-white group-hover:rotate-12"
      )} />

      {!isCollapsed && (
        <>
          <span className={cn(
            "flex-1 truncate uppercase tracking-[0.15em] text-[10px] font-black",
            isActive ? "text-primary" : "text-white"
          )}>
            {label}
          </span>
          {badge !== undefined && badge > 0 && (
            <span className={cn(
              "h-5 min-w-[20px] px-1 rounded-lg text-[9px] font-black grid place-items-center leading-none shrink-0 shadow-lg",
              isActive ? "bg-primary text-white" : "bg-white text-primary"
            )}>
              {badge > 99 ? "99+" : badge}
            </span>
          )}
          {tag && (
            <span className={cn(
              "text-[8px] px-2 py-0.5 rounded-md font-black shrink-0 uppercase tracking-tighter border",
              isActive ? "bg-primary/10 text-primary border-primary/20" : "bg-white/20 text-white border-white/30"
            )}>
              {tag}
            </span>
          )}
        </>
      )}
      
      {/* Active Indicator Glow */}
      {isActive && (
        <div className="absolute inset-0 bg-primary/5 animate-pulse-slow pointer-events-none" />
      )}
    </NavLink>
  );

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          {content}
        </TooltipTrigger>
        <TooltipContent side="right" className="font-bold text-[11px] bg-primary text-white border-none shadow-2xl px-4 py-2 rounded-xl">
          <div className="flex flex-col">
            <span className="uppercase tracking-widest text-[9px] opacity-70 mb-0.5">Navegar para</span>
            <span>{label}</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const SidebarContent = ({ 
  mobile = false, isDark, hasData, isAdmin, location, setIsMobileMenuOpen, sidebarInfo, isCollapsed, setIsCollapsed
}: any) => {
  const { t } = useLanguage();
  return (
  <div className={cn(
    "flex flex-col h-full overflow-visible transition-all duration-300 w-full",
    mobile ? "bg-background p-4" : "p-2"
  )}>
    {/* Logo Bento Card */}
    <div className="mb-3 relative group/logo">
      <Link to="/upload" className={cn(
        "flex items-center rounded-2xl glass-card border-primary/10 hover:border-primary/30 transition-all group overflow-hidden",
        isCollapsed ? "p-2 justify-center" : "gap-3 p-3"
      )}>
        <div className={cn(
          "rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/20 group-hover:rotate-12 transition-all shrink-0",
          isCollapsed ? "size-10" : "size-9"
        )}>
          <Database className={cn("text-white", isCollapsed ? "size-6" : "size-5")} />
        </div>
        {!isCollapsed && (
          <div className="flex flex-col">
            <span className="font-black text-[13px] tracking-tight leading-none">DataGrid XML</span>
            <span className="text-[9px] font-black uppercase tracking-widest text-primary mt-1">Analytical Engine</span>
          </div>
        )}
      </Link>
    </div>

    {/* Nav scrollable */}
    <nav className={cn(
      "flex-1 overflow-y-auto space-y-1 custom-scrollbar",
      isCollapsed ? "pr-0" : "pr-2"
    )}>
      <SectionTitle isCollapsed={isCollapsed}>{t("Ingestão & Carga")}</SectionTitle>
      <NavItem to="/upload" label={t("Importação de XML")} icon={Upload} isActive={location.pathname === "/upload"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/importacoes" label={t("Logs de Importação")} icon={History} isActive={location.pathname === "/importacoes"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />

      <SectionTitle isCollapsed={isCollapsed}>{t("Analytics & Métricas")}</SectionTitle>
      <NavItem to="/dashboard" label={t("Dashboard de Dados")} icon={LayoutDashboard} isActive={location.pathname === "/dashboard"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/insights" label={t("Métricas de Impostos")} icon={BarChart3} isActive={location.pathname === "/insights"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/analytics" label={t("Cruzamento de Dados")} icon={LineChart} tag="PRO" isActive={location.pathname === "/analytics"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/relatorio" label={t("Sintético de Operações")} icon={FileBarChart2} isActive={location.pathname === "/relatorio"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />

      <SectionTitle isCollapsed={isCollapsed}>{t("Navegação de Bases")}</SectionTitle>
      <NavItem to="/notas" label={t("Listagem de Documentos")} icon={FileText} isActive={location.pathname === "/notas"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/fornecedores" label={t("Fornecedores (Entrada)")} icon={Users} isActive={location.pathname === "/fornecedores"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/clientes" label={t("Clientes (Saída)")} icon={Users} isActive={location.pathname === "/clientes"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/financeiro" label={t("Faturas e Duplicatas")} icon={CreditCard} isActive={location.pathname === "/financeiro"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/transporte" label={t("Logística & Transporte")} icon={Building2} isActive={location.pathname === "/transporte"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/produtos" label={t("Índice de Produtos")} icon={Package} isActive={location.pathname === "/produtos"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/tributacao" label={t("Tributação Extraída")} icon={TrendingUp} isActive={location.pathname === "/tributacao"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/cfop" label={t("Classificação CFOP")} icon={Tag} isActive={location.pathname === "/cfop"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/classificacao" label={t("Classificação NCM")} icon={Tag} tag="AI" isActive={location.pathname === "/classificacao"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />

      <SectionTitle isCollapsed={isCollapsed}>{t("Verificação Técnica")}</SectionTitle>
      <NavItem to="/validacao-xml" label={t("Integridade de Schema")} icon={FileSearch} isActive={location.pathname === "/validacao-xml"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/divergencias" label={t("Assimetria de Valores")} icon={GitBranch} isActive={location.pathname === "/divergencias"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/inspecao" label={t("Logs de Parâmetros")} icon={ShieldCheck} isActive={location.pathname === "/inspecao"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/alertas" label={t("Ocorrências de Sistema")} icon={BellRing} isActive={location.pathname === "/alertas"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />

      <SectionTitle isCollapsed={isCollapsed}>{t("Operacional")}</SectionTitle>
      <NavItem to="/exportacoes" label={t("Exportação em Lote")} icon={Download} tag="CSV" isActive={location.pathname === "/exportacoes"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
      <NavItem to="/dashboard-local" label={t("Sincronização Offline")} icon={Database} tag="DB" isActive={location.pathname === "/dashboard-local"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />

      {/* Support Hub Card */}
      <div className={cn(
        "mt-6 mx-2 mb-6 p-5 rounded-[2rem] glass-technical border-primary/20 bg-primary/5 relative overflow-hidden group transition-all duration-500 hover:border-primary/40 shadow-2xl shadow-primary/5",
        isCollapsed && "mx-auto w-12 p-0 h-12 flex items-center justify-center rounded-2xl bg-primary/10 border-primary/20 hover:bg-primary transition-all duration-300"
      )}>
        {isCollapsed ? (
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Link to="/ajuda" className="flex items-center justify-center w-full h-full relative">
                  <HelpCircle className={cn("size-5 transition-all duration-500", location.pathname === "/ajuda" ? "text-white" : "text-primary group-hover:scale-110 group-hover:rotate-12")} />
                  {location.pathname !== "/ajuda" && (
                    <div className="absolute top-0 right-0 size-1.5 rounded-full bg-primary animate-ping" />
                  )}
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-bold text-[11px] bg-primary text-white border-none shadow-2xl px-4 py-2 rounded-xl">
                {t("Central de Ajuda")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <>
            <div className="absolute -right-6 -top-6 opacity-[0.05] group-hover:opacity-[0.1] transition-all duration-1000 rotate-12 group-hover:rotate-45">
              <HelpCircle className="size-24" />
            </div>
            <div className="relative z-10 flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-xl bg-primary text-white flex items-center justify-center shadow-lg shadow-primary/40 group-hover:rotate-12 transition-transform duration-500 relative">
                  <Sparkles className="size-4.5" />
                  <div className="absolute -inset-1 rounded-xl bg-primary/20 animate-pulse" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[11px] font-black uppercase tracking-tight leading-none">{t("Suporte")}</span>
                  <span className="text-[8px] font-black text-primary uppercase tracking-[0.2em] opacity-80 mt-1">AI-CORE ACTIVE</span>
                </div>
              </div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase leading-snug opacity-80 tracking-tight">
                Consulte a documentação técnica do Analytical Engine.
              </p>
              <Link to="/ajuda">
                <Button size="sm" className={cn(
                  "w-full h-10 rounded-xl text-[10px] font-black uppercase tracking-[0.15em] transition-all active:scale-95 shadow-xl",
                  location.pathname === "/ajuda" 
                    ? "bg-white text-primary hover:bg-white/90 shadow-white/20" 
                    : "bg-primary text-white hover:bg-primary/90 shadow-primary/20"
                )}>
                  {t("Acessar Hub")}
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>

      {isAdmin && (
        <>
          <SectionTitle isCollapsed={isCollapsed}>{t("Sistema")}</SectionTitle>
          <NavItem to="/admin" label={t("Configurações Gerais")} icon={Settings} isActive={location.pathname === "/admin"} setIsMobileMenuOpen={setIsMobileMenuOpen} isCollapsed={isCollapsed} />
        </>
      )}
    </nav>

    {/* Sidebar Toggle Footer - Fixed at bottom */}
    {!mobile && (
      <div className="mt-auto py-2 flex items-center justify-center relative border-t border-foreground/[0.05]">
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={cn(
            "size-6 rounded-full bg-primary text-white shadow-lg grid place-items-center transition-all duration-500 z-50 hover:scale-110 ring-2 ring-background",
            isCollapsed && "rotate-180"
          )}
          title={isCollapsed ? t("Expandir") : t("Recolher")}
        >
          <ChevronRight className="size-3" />
        </button>
      </div>
    )}
  </div>
  );
};

function ClockComponent() {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    document.title = "Faturas e Duplicatas — DataGrid XML";
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex items-center gap-4 px-4 py-2 rounded-xl glass-card border-primary/10">
      <div className="flex items-center gap-2 border-r border-foreground/5 pr-4">
        <Calendar className="size-3.5 text-primary" />
        <span className="text-[11px] font-black tracking-tight uppercase">
          {now.toLocaleDateString("pt-BR", { day: '2-digit', month: 'short' })}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Clock className="size-3.5 text-primary" />
        <span className="text-[11px] font-mono font-black tabular-nums">
          {now.toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}

import { LocalSyncWatcher } from "@/components/LocalSyncWatcher";

export default function AppShell() {
  const [sidebarInfo, setSidebarInfo] = useState({
    version: "v2.5.0 · Núcleo de IA",
    dpo_name: "Encarregado de Dados (DPO)",
    dpo_email: "privacidade@extratorfiscal.ai",
    warning: "Validado por Profissionais de CRC."
  });

  const dashboard = useFiscalStore((s) => s.dashboard);
  const uploadHistory = useFiscalStore((s) => s.uploadHistory);
  const reprocessarPorCnpj = useFiscalStore((s) => s.reprocessarPorCnpj);
  
  // Extrai entidades únicas do histórico de importação para o seletor (unificando por CNPJ limpo)
  const entidades = Array.from(new Map(uploadHistory
    .filter(h => h.entidadeCnpj && h.entidadeCnpj !== "")
    .map(h => {
      const cleanCnpj = h.entidadeCnpj?.replace(/\D/g, "") || "";
      return [cleanCnpj, { nome: h.entidadeNome, cnpj: h.entidadeCnpj }];
    }))
    .values());

  useFolderSync();
  const hasData = !!dashboard;
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isAdmin } = useAuth();
  const [periodoLocal, setPeriodoLocal] = useState({ inicio: "", fim: "" });

  useEffect(() => {
    if (dashboard?.stats?.periodo) {
      setPeriodoLocal({
        inicio: useFiscalStore.getState().filtroPeriodo?.inicio || dashboard.stats.periodo.inicio || "",
        fim: useFiscalStore.getState().filtroPeriodo?.fim || dashboard.stats.periodo.fim || ""
      });
    }
  }, [dashboard]);
  const { theme, toggle } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const isDark = theme === "dark";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(MOTIVATIONAL_QUOTES[0]);
  const [isInitializing, setIsInitializing] = useState(false); // Watcher cuida da carga inicial

  useEffect(() => {
    const updateQuote = () => {
      const idx = Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length);
      setCurrentQuote(MOTIVATIONAL_QUOTES[idx]);
    };
    updateQuote();
    const interval = setInterval(updateQuote, 30 * 1000); // 30 segundos
    return () => clearInterval(interval);
  }, []);

  const privacyMode = useFiscalStore((s) => s.privacyMode);
  const togglePrivacyMode = useFiscalStore((s) => s.togglePrivacyMode);
  const purgeAllData = useFiscalStore((s) => s.purgeAllData);

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  async function handleLogout() {
    setIsLoggingOut(true);
    try {
      // 1. Limpa o store do Zustand (incluindo persistência)
      purgeAllData();
      localStorage.removeItem("fiscal-app-storage");
      
      // 2. Limpa tokens do Supabase manualmente para garantir que não logue sozinho (esquecer sessão)
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith("sb-")) localStorage.removeItem(key);
      });
      
      // 3. Chama o signOut do contexto (limpa session/roles)
      await signOut();
      
      // 4. Força redirecionamento físico para limpar o estado do React
      window.location.href = "/login";
    } catch (err) {
      console.error("[AppShell] Erro no logout:", err);
      window.location.href = "/login";
    }
  }

  const handleNewSession = () => {
    if (window.confirm("Resetar sessão? Todos os dados serão apagados.")) {
      purgeAllData();
      navigate("/upload", { replace: true });
    }
  };

  const lastSyncTime = useFiscalStore(s => s.lastSyncTime);
  const syncProgress = useFiscalStore(s => s.syncProgress);
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  return (
    <div className="h-screen overflow-hidden flex flex-col lg:flex-row bg-background">
      <LocalSyncWatcher />
      <LegalConsentModal />
      
      {/* Sidebar Desktop */}
      <aside className={cn(
        "hidden lg:flex shrink-0 flex-col overflow-visible border-r border-foreground/[0.05] bg-muted/5 dark:bg-slate-950/80 backdrop-blur-xl transition-all duration-300 ease-in-out",
        isCollapsed ? "w-20" : "w-80"
      )}>
        <SidebarContent 
          isDark={isDark} 
          hasData={hasData} 
          isAdmin={isAdmin} 
          location={location} 
          setIsMobileMenuOpen={setIsMobileMenuOpen} 
          sidebarInfo={sidebarInfo}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
        />
      </aside>

      {/* Mobile Top Header */}
      <header className="lg:hidden h-16 shrink-0 flex items-center justify-between px-4 border-b border-foreground/[0.05] glass z-50">
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-xl bg-primary grid place-items-center shadow-lg shadow-primary/20">
            <Database className="size-5 text-white" />
          </div>
          <span className="font-black text-sm tracking-tight uppercase">DataGrid XML</span>
        </div>
        
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-xl">
              <Menu className="size-6 text-primary" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 border-none w-72">
            <SidebarContent 
              mobile 
              isDark={isDark} 
              hasData={hasData} 
              isAdmin={isAdmin} 
              location={location} 
              setIsMobileMenuOpen={setIsMobileMenuOpen} 
              sidebarInfo={sidebarInfo} 
            />
          </SheetContent>
        </Sheet>
      </header>

      <main className="flex-1 flex flex-col min-w-0 h-full relative overflow-hidden">
        {/* Control Center Header */}
        <header className="h-16 shrink-0 flex items-center px-4 lg:px-6 z-40 gap-4 border-b border-foreground/[0.05] bg-background/50 backdrop-blur-xl sticky top-0">
          <div className="flex items-center gap-6 flex-1">
            {/* Global Search */}
            <div className="relative max-w-sm w-full group hidden md:block">
              <FileSearch className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <input 
                type="text" 
                placeholder={t("Buscar XML, CNPJ, Produto...")} 
                className="w-full h-9 pl-9 pr-4 rounded-xl bg-muted/5 border border-foreground/[0.05] focus:border-primary/50 focus:ring-4 focus:ring-primary/5 outline-none text-[11px] font-bold transition-all"
              />
            </div>
            
            {/* Entity Selector */}
            <div className="hidden xl:flex items-center gap-3 border-l border-foreground/[0.05] pl-6 max-w-xs w-full">
              <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Building2 className="size-4" />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-[8px] uppercase font-black tracking-widest text-muted-foreground leading-none mb-1">Entidade em Inspeção</span>
                <select 
                  className="bg-transparent border-none outline-none text-[10px] font-black uppercase tracking-tight text-foreground/90 cursor-pointer w-full truncate"
                  value={dashboard?.cnpjEmpresa || ""}
                  onChange={(e) => reprocessarPorCnpj(e.target.value)}
                >
                  <option value="" className="bg-background text-foreground">Geral / Todas</option>
                  {entidades.map((ent) => (
                    <option key={ent.cnpj} value={ent.cnpj} className="bg-background text-foreground">
                      {ent.nome || "Empresa Desconhecida"}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Telemetry Stats */}
            <div className="hidden lg:flex items-center gap-10 border-l border-foreground/[0.05] pl-10">
              <div className="flex flex-col">
                <span className="text-[8px] uppercase font-black tracking-widest text-muted-foreground leading-none mb-1">{t("Base Local")}</span>
                <div className="flex items-center gap-2">
                  <Database className="size-3 text-primary" />
                  <span className="text-[10px] font-black tabular-nums">{(dashboard?.stats?.totalNotas || 0).toLocaleString("pt-BR")} <span className="text-muted-foreground font-bold">XMLs</span></span>
                  {lastSyncTime && (
                    <div className="flex items-center gap-1.5 ml-1 border-l border-foreground/5 pl-2">
                      <div className={cn("size-1.5 rounded-full bg-emerald-500", syncProgress ? "animate-spin" : "animate-pulse")} />
                      <span className="text-[8px] font-black opacity-40 tabular-nums">
                        {syncProgress && syncProgress.total > 0 ? `${((syncProgress.current / syncProgress.total) * 100).toFixed(0)}%` : lastSyncTime}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {/* Os blocos Web Worker, Segurança e Qualidade foram removidos para simplificação conforme solicitado */}

                  <div className="flex flex-col border-l border-foreground/[0.05] pl-10">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[8px] uppercase font-black tracking-widest text-muted-foreground leading-none">{t("Período de Referência")}</span>
                      <div className="flex gap-2 ml-4">
                         <button 
                           onClick={() => {
                             const now = new Date();
                             const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
                             const end = now.toISOString().split('T')[0];
                             setPeriodoLocal({ inicio: start, fim: end });
                             useFiscalStore.getState().setFiltroPeriodo(start, end);
                           }}
                           className="text-[7px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
                         >
                           Mês Atual
                         </button>
                         <button 
                           onClick={() => {
                             const now = new Date();
                             const end = now.toISOString().split('T')[0];
                             const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                             setPeriodoLocal({ inicio: start, fim: end });
                             useFiscalStore.getState().setFiltroPeriodo(start, end);
                           }}
                           className="text-[7px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
                         >
                           30 Dias
                         </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 bg-muted/5 border border-foreground/5 rounded-xl px-3 py-1.5 focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/5 transition-all group">
                        <Calendar className="size-3.5 text-orange-500" />
                        <div className="flex items-center gap-1">
                          <input 
                            type="date"
                            className="bg-transparent border-none outline-none text-[11px] font-black uppercase tracking-tighter w-28 text-foreground/90 focus:text-primary transition-colors"
                            value={periodoLocal.inicio}
                            onChange={(e) => setPeriodoLocal(prev => ({ ...prev, inicio: e.target.value }))}
                          />
                          <span className="text-[10px] font-black opacity-20 mx-1">→</span>
                          <input 
                            type="date"
                            className="bg-transparent border-none outline-none text-[11px] font-black uppercase tracking-tighter w-28 text-foreground/90 focus:text-primary transition-colors"
                            value={periodoLocal.fim}
                            onChange={(e) => setPeriodoLocal(prev => ({ ...prev, fim: e.target.value }))}
                          />
                        </div>
                        <div className="w-px h-4 bg-foreground/10 mx-1" />
                        <button 
                          onClick={() => {
                            if (periodoLocal.inicio && periodoLocal.fim) {
                              useFiscalStore.getState().setFiltroPeriodo(periodoLocal.inicio, periodoLocal.fim);
                              // Força uma sincronização completa com o banco para garantir recálculo
                              import("@/lib/local/sync").then(m => m.syncLocalDBToStore());
                              toast.success(`Período sincronizado: ${periodoLocal.inicio} até ${periodoLocal.fim}`);
                            }
                          }}
                          className="p-1.5 hover:bg-primary/10 rounded-lg transition-all group active:scale-95"
                          title="Sincronizar Período"
                        >
                          <RefreshCw className="size-3.5 text-primary group-hover:rotate-180 transition-transform duration-700" />
                        </button>
                      </div>
                    </div>
                  </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden xl:block">
              <ClockComponent />
            </div>

            <div className="flex items-center gap-1.5 px-1 py-1 rounded-xl bg-muted/5 border border-foreground/[0.05]">
              <button
                onClick={toggleLanguage}
                className="h-8 px-2 rounded-lg hover:bg-primary/10 hover:text-primary transition-all font-black text-[9px] uppercase"
              >
                {language === 'pt-BR' ? 'PT' : 'EN'}
              </button>
              <button
                onClick={toggle}
                className="size-8 rounded-lg grid place-items-center transition-all hover:bg-primary/10 hover:text-primary"
              >
                {isDark ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
              </button>
            </div>

            <div className="h-8 w-px bg-foreground/5 mx-1" />

            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className={cn(
                "h-9 px-3 rounded-xl glass border-red-500/10 text-red-500 font-black text-[9px] uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all flex items-center gap-2",
                isLoggingOut && "opacity-70 cursor-wait"
              )}
            >
              {isLoggingOut ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <LogOut className="size-3.5" />
              )}
              {isLoggingOut ? t("Saindo...") : t("Sair")}
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto scroll-smooth p-4 lg:p-6 pt-0 lg:pt-0 relative">
          <div className="max-w-[1600px] mx-auto pb-12 relative z-10">
            <Outlet />
          </div>
          
          {/* Watermark Privacy */}
          {privacyMode && (
            <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center overflow-hidden opacity-[0.02]">
              <div className="rotate-[-35deg] text-center whitespace-nowrap">
                {Array.from({ length: 20 }).map((_, i) => (
                  <div key={i} className="text-6xl font-black mb-12 select-none uppercase tracking-tighter">
                    Restricted Access • {user?.email} • {new Date().toLocaleDateString()}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
